import { z } from 'zod';
import { toast } from 'sonner';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useResetPasswordForm } from '../useAuthForm';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useResetPasswordMutation } from '../auth.api.slice';
import useSearchParamActions from '@/customHooks/useSearchParamActions';
import NotFound from '@/pages/NotFound';
import { resetPasswordSchema } from '../auth.schema';
import { setShowLoginDialog } from '@/redux/global/global.slice';

function ResetPasswordForm() {
  const resetPasswordForm = useResetPasswordForm();
  const [resetPassword, { isLoading: isResetPasswordLoading }] =
    useResetPasswordMutation();
  const { getSearchParam } = useSearchParamActions();
  const resetToken = getSearchParam('resetToken');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  async function handleResetPasswordSubmit(
    values: z.infer<typeof resetPasswordSchema>
  ) {
    if (!resetToken) return;
    try {
      const data = await resetPassword({
        password: values.password,
        token: resetToken,
      }).unwrap();

      if (data.status !== 'success') {
        toast.error('Reset Password Failed', {
          description: (data as any)?.errorMessage || 'Something went wrong',
        });
      }

      navigate('/');
      dispatch(setShowLoginDialog(true));
      toast.success('Reset Password successful');
    } catch (error: any) {
      toast.error('Reset Password Failed', {
        description: error?.data?.errorMessage || 'Something went wrong',
      });
    }
  }

  if (!resetToken) {
    return <NotFound />;
  }

  return (
    <Form {...resetPasswordForm}>
      <form
        onSubmit={resetPasswordForm.handleSubmit(handleResetPasswordSubmit)}
      >
        <div>
          <h1 className="font-primary text-lg font-bold text-center">
            Reset Your Password !
          </h1>
          <p className="text-center font-secondary text-[16px] text-[#666666]">
            Please enter your password below to reset it.
          </p>
        </div>
        <div className="flex flex-col p-2 gap-4">
          <div className="flex flex-col gap-2">
            <FormField
              control={resetPasswordForm.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder="Enter your password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex flex-col gap-2">
            <FormField
              control={resetPasswordForm.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder="Confirm your password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="mt-2">
          <div className="flex flex-col gap-2 w-full">
            <Button
              type="submit"
              className="w-full bg-black font-bold"
              disabled={isResetPasswordLoading}
            >
              Change Password
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}

export default ResetPasswordForm;
